import React, { useState, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router-dom";
import {
  useGetLeadsQuery,
  useGetLeadQuery,
  useUpdateLeadStatusMutation,
  useAddLeadCommunicationMutation,
  useConvertLeadMutation,
  useDeleteLeadMutation,
} from "../../../Api/attoDeskApi";
import {
  ILead,
  LeadStatus,
  ILeadCommunication,
} from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneAndroid";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import HistoryIcon from "@mui/icons-material/History";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import TagIcon from "@mui/icons-material/Tag";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import axios from "axios";
import * as XLSX from "xlsx";
import CalendarView from "../Calendar";

dayjs.extend(relativeTime);

// ── Design tokens ──────────────────────────────────────────────────────────────
const BRAND  = "#2f3891";
const BORDER = "#e2e8f0";

const thSx = {
  fontWeight: 700, fontSize: 11, color: "#64748b",
  textTransform: "uppercase" as const, letterSpacing: "0.06em",
  py: 1.5, px: 1.5, borderBottom: `2px solid ${BORDER}`, bgcolor: "#f8fafc",
  whiteSpace: "nowrap" as const,
};
const tdSx = {
  py: 1.25, px: 1.5, borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#1e293b", verticalAlign: "middle" as const,
};

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<LeadStatus, { label: string; color: string; bg: string; border: string }> = {
  new:       { label: "New",       color: "#2f3891", bg: "#eef2ff", border: "#c7d2fe" },
  contacted: { label: "Contacted", color: "#f97316", bg: "#fff7ed", border: "#fed7aa" },
  qualified: { label: "Qualified", color: "#a855f7", bg: "#faf5ff", border: "#e9d5ff" },
  lost:      { label: "Lost",      color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
  converted: { label: "Converted", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
};

const EVENT_TYPE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  school:    { label: "School",    color: "#0ea5e9", bg: "#f0f9ff" },
  wedding:   { label: "Wedding",   color: "#ec4899", bg: "#fdf2f8" },
  corporate: { label: "Corporate", color: "#2f3891", bg: "#eef2ff" },
  other:     { label: "Other",     color: "#64748b", bg: "#f8fafc" },
};

const SOURCE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  online: { label: "Online", color: "#0ea5e9", bg: "#f0f9ff" },
  manual: { label: "Admin",  color: "#64748b", bg: "#f8fafc" },
  call:   { label: "Call",   color: "#f59e0b", bg: "#fffbeb" },
};

// Which website form the enquiry came from. The Contact Form has no badge:
// it is the ordinary case, and a badge on every row says nothing.
const FORM_TYPE_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  charter_quote: { label: "Charter Quote", color: "#dc2626", bg: "#fef2f2" },
};

const COMM_TYPE_CONFIG = {
  email: { label: "Email", color: "#2f3891", bg: "#eef2ff" },
  sms:   { label: "SMS",   color: "#0ea5e9", bg: "#f0f9ff" },
  call:  { label: "Call",  color: "#f59e0b", bg: "#fffbeb" },
  note:  { label: "Note",  color: "#64748b", bg: "#f8fafc" },
};

const ALL_STATUSES: LeadStatus[] = ["new", "contacted", "qualified", "lost", "converted"];

// ── Initials avatar ────────────────────────────────────────────────────────────
const getInitials = (firstname: string, lastname: string | null) =>
  `${firstname[0] || "?"}${lastname?.[0] || ""}`.toUpperCase();

// ── Export to Excel ────────────────────────────────────────────────────────────
const exportToExcel = (leads: ILead[]) => {
  const data = leads.map((l, i) => ({
    "#":           i + 1,
    "Ref":         l.lead_ref || "",
    "Name":        `${l.contact.firstname} ${l.contact.lastname || ""}`.trim(),
    "Phone":       l.contact.phone || "",
    "Email":       l.contact.email || "",
    "Event Type":  l.contact.event_type || "",
    "Event Date":  l.contact.event_date || "",
    "Pickup":      l.contact.route_pickup || "",
    "Dropoff":     l.contact.route_dropoff || "",
    "Status":      l.status,
    "Source":      l.source || "",
    "Form":        FORM_TYPE_CONFIG[l.form_type || ""]?.label || "Contact Form",
    "Created":     dayjs(l.created_at).format("YYYY-MM-DD"),
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({ wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
  XLSX.writeFile(wb, `enquiries_${dayjs().format("YYYYMMDD")}.xlsx`);
};

// ── Export to PDF (uses backend DomPDF — matches Invoice Report layout) ───────

// ── Detail Modal ───────────────────────────────────────────────────────────────
const LeadDetailModal: React.FC<{
  leadId: number | null;
  onClose: () => void;
  onConvertToQuote: (lead: ILead) => void;
}> = ({ leadId, onClose, onConvertToQuote }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [commType, setCommType]     = useState<"email" | "sms" | "call" | "note">("note");
  const [commMsg, setCommMsg]       = useState("");
  const [addingComm, setAddingComm] = useState(false);
  const [converting, setConverting] = useState(false);
  const [activeTab, setActiveTab]   = useState<"communications" | "history">("communications");

  const { data, isLoading, refetch } = useGetLeadQuery(leadId!, { skip: !leadId });
  const [updateStatus]   = useUpdateLeadStatusMutation();
  const [addComm]        = useAddLeadCommunicationMutation();
  const [convertLead]    = useConvertLeadMutation();

  const lead: ILead | null = (data as any)?.data ?? null;

  const handleStatusChange = async (newStatus: string) => {
    if (!lead) return;
    const res: any = await updateStatus({ id: lead.id, status: newStatus });
    if (res?.error || res?.data?.success === false) {
      showErrorMessage(res?.data?.message || "Failed to update status.");
    } else {
      showMessage(`Status updated to "${newStatus}".`);
      refetch();
    }
  };

  const handleAddComm = async () => {
    if (!lead || !commMsg.trim()) return;
    setAddingComm(true);
    const res: any = await addComm({ id: lead.id, type: commType, message: commMsg.trim() });
    if (res?.error || res?.data?.success === false) {
      showErrorMessage(res?.data?.message || "Failed to log communication.");
    } else {
      showMessage("Communication logged.");
      setCommMsg("");
      refetch();
    }
    setAddingComm(false);
  };

  const handleConvert = async () => {
    if (!lead) return;
    setConverting(true);
    const res: any = await convertLead(lead.id);
    if (res?.error || res?.data?.success === false) {
      showErrorMessage(res?.data?.message || "Failed to convert lead.");
    } else {
      showMessage("Lead converted to customer.");
      refetch();
    }
    setConverting(false);
  };

  const quickStatusBtns: { label: string; status: LeadStatus; color: string }[] = [
    { label: "Mark Contacted", status: "contacted", color: "#f97316" },
    { label: "Mark Qualified", status: "qualified", color: "#a855f7" },
    { label: "Mark Lost",      status: "lost",      color: "#ef4444" },
  ];

  return (
    <Dialog open={!!leadId} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}>
      {isLoading || !lead ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* ── Header ── */}
          <DialogTitle sx={{ pb: 0 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Avatar sx={{ width: 48, height: 48, bgcolor: "#eef2ff", color: BRAND, fontWeight: 800, fontSize: 17, borderRadius: 2 }}>
                {getInitials(lead.contact.firstname, lead.contact.lastname)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>
                    {lead.contact.firstname} {lead.contact.lastname || ""}
                  </Typography>
                  <Chip label={lead.lead_ref} size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 700, color: BRAND, bgcolor: "#eef2ff", border: "1px solid #c7d2fe" }} />
                  <Box component="span"
                    sx={{ display: "inline-flex", alignItems: "center", px: 1.25, py: 0.25, borderRadius: 1.5,
                      fontSize: 11, fontWeight: 700, color: STATUS_CONFIG[lead.status].color,
                      bgcolor: STATUS_CONFIG[lead.status].bg, border: `1px solid ${STATUS_CONFIG[lead.status].border}` }}>
                    {STATUS_CONFIG[lead.status].label}
                  </Box>
                </Box>
                <Box sx={{ display: "flex", gap: 2, mt: 0.5, flexWrap: "wrap" }}>
                  {lead.contact.email && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <EmailOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>{lead.contact.email}</Typography>
                    </Box>
                  )}
                  {lead.contact.phone && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>{lead.contact.phone}</Typography>
                    </Box>
                  )}
                  <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                    {dayjs(lead.created_at).fromNow()}
                  </Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={onClose} sx={{ mt: -0.5 }}>
                <CancelOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider sx={{ mt: 2 }} />

          <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* ── Two-column info ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>

              {/* Customer Info */}
              <Box sx={{ bgcolor: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                  Customer Info
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonOutlineIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: 13, color: "#0f172a", fontWeight: 600 }}>
                      {lead.contact.firstname} {lead.contact.lastname || ""}
                    </Typography>
                  </Box>
                  {lead.contact.phone && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PhoneOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      <Typography
                        component="a" href={`tel:${lead.contact.phone}`}
                        sx={{ fontSize: 13, color: "#0ea5e9", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                        {lead.contact.phone}
                      </Typography>
                      {lead.contact.phone && (
                        <Tooltip title="WhatsApp" arrow>
                          <IconButton size="small"
                            component="a" href={`https://wa.me/${lead.contact.phone.replace(/\D/g, "")}`} target="_blank"
                            sx={{ p: 0.25, color: "#25d366" }}>
                            <WhatsAppIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                  {lead.contact.email && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      <Typography
                        component="a" href={`mailto:${lead.contact.email}`}
                        sx={{ fontSize: 13, color: "#0ea5e9", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                        {lead.contact.email}
                      </Typography>
                    </Box>
                  )}
                  {lead.source && SOURCE_CONFIG[lead.source] && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Source:</Typography>
                      <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1, py: 0.2, borderRadius: 1,
                        color: SOURCE_CONFIG[lead.source].color, bgcolor: SOURCE_CONFIG[lead.source].bg }}>
                        {SOURCE_CONFIG[lead.source].label}
                      </Box>
                    </Box>
                  )}
                  {lead.form_type && FORM_TYPE_CONFIG[lead.form_type] && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Form:</Typography>
                      <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1, py: 0.2, borderRadius: 1,
                        color: FORM_TYPE_CONFIG[lead.form_type].color, bgcolor: FORM_TYPE_CONFIG[lead.form_type].bg }}>
                        {FORM_TYPE_CONFIG[lead.form_type].label}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Trip Details */}
              <Box sx={{ bgcolor: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
                  Trip Details
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {lead.contact.event_type && EVENT_TYPE_CONFIG[lead.contact.event_type] && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>Event:</Typography>
                      <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: 1, py: 0.2, borderRadius: 1,
                        color: EVENT_TYPE_CONFIG[lead.contact.event_type].color,
                        bgcolor: EVENT_TYPE_CONFIG[lead.contact.event_type].bg }}>
                        {EVENT_TYPE_CONFIG[lead.contact.event_type].label}
                      </Box>
                    </Box>
                  )}
                  {lead.contact.event_date && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 13, color: "#374151" }}>
                        {dayjs(lead.contact.event_date).format("DD MMM YYYY")}
                      </Typography>
                    </Box>
                  )}
                  {(lead.contact.route_pickup || lead.contact.route_dropoff) && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <RouteOutlinedIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                      <Typography sx={{ fontSize: 13, color: "#374151" }}>
                        {lead.contact.route_pickup || "—"}
                        {" → "}
                        {lead.contact.route_dropoff || "—"}
                      </Typography>
                    </Box>
                  )}
                  {!lead.contact.event_type && !lead.contact.event_date && !lead.contact.route_pickup && (
                    <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>No trip details provided.</Typography>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Initial Message */}
            {lead.contact.message && (
              <Box sx={{ bgcolor: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.75 }}>
                  Inquiry Note
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>{lead.contact.message}</Typography>
              </Box>
            )}

            {/* Quick action buttons */}
            <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap", alignItems: "center" }}>
              {quickStatusBtns.map((b) => (
                lead.status !== b.status && lead.status !== "converted" && (
                  <Button key={b.status} size="small" variant="outlined"
                    onClick={() => handleStatusChange(b.status)}
                    sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600,
                      color: b.color, borderColor: b.color, "&:hover": { bgcolor: `${b.color}10` } }}>
                    {b.label}
                  </Button>
                )
              ))}
              {lead.status !== "converted" && (
                <Button size="small" variant="contained"
                  disabled={converting}
                  startIcon={converting ? <CircularProgress size={14} /> : <CheckCircleOutlineIcon />}
                  onClick={handleConvert}
                  sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}>
                  Convert to Customer
                </Button>
              )}
              <Button size="small" variant="contained"
                startIcon={<RocketLaunchOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => onConvertToQuote(lead)}
                sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, ml: "auto",
                  bgcolor: BRAND, "&:hover": { bgcolor: "#2f3891" } }}>
                Convert to Quote
              </Button>
            </Box>

            {/* Status update dropdown */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                  Update Status
                </Typography>
                <Select size="small" value={lead.status} onChange={(e) => handleStatusChange(e.target.value)}
                  sx={{ minWidth: 180, borderRadius: 2, fontSize: 13, height: 36,
                    color: STATUS_CONFIG[lead.status].color,
                    bgcolor: STATUS_CONFIG[lead.status].bg,
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: STATUS_CONFIG[lead.status].border } }}>
                  {ALL_STATUSES.map((s) => (
                    <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{STATUS_CONFIG[s].label}</MenuItem>
                  ))}
                </Select>
              </Box>
              {lead.customer && (
                <Box sx={{ bgcolor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 2, px: 2, py: 0.75, mt: 2.5 }}>
                  <Typography sx={{ fontSize: 12, color: "#16a34a", fontWeight: 700 }}>✓ Converted — {lead.customer.name}</Typography>
                </Box>
              )}
            </Box>

            {/* Log Communication */}
            <Box sx={{ bgcolor: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: 2, p: 2 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
                Log Communication
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Select size="small" value={commType} onChange={(e) => setCommType(e.target.value as any)}
                  sx={{ minWidth: 110, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff" }}>
                  {(["note", "call", "email", "sms"] as const).map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{COMM_TYPE_CONFIG[t].label}</MenuItem>
                  ))}
                </Select>
                <TextField size="small" fullWidth multiline rows={2}
                  value={commMsg} onChange={(e) => setCommMsg(e.target.value)}
                  placeholder="Add a note, call summary, email sent..." sx={{ bgcolor: "#fff" }} />
                <Button variant="contained" size="small"
                  disabled={!commMsg.trim() || addingComm}
                  startIcon={addingComm ? <CircularProgress size={14} /> : <AddCommentOutlinedIcon />}
                  onClick={handleAddComm}
                  sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12,
                    bgcolor: BRAND, "&:hover": { bgcolor: "#2f3891" }, whiteSpace: "nowrap", mt: 0.5 }}>
                  Log
                </Button>
              </Box>
            </Box>

            {/* Tabs: Communications | History */}
            <Box>
              <Box sx={{ display: "flex", gap: 0, borderBottom: `2px solid ${BORDER}`, mb: 2 }}>
                {(["communications", "history"] as const).map((tab) => (
                  <Button key={tab} size="small" onClick={() => setActiveTab(tab)}
                    startIcon={tab === "communications"
                      ? <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                      : <HistoryIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      textTransform: "none", fontSize: 12, fontWeight: 600, borderRadius: 0, pb: 1,
                      borderBottom: activeTab === tab ? `2px solid ${BRAND}` : "2px solid transparent",
                      mb: "-2px", color: activeTab === tab ? BRAND : "#64748b",
                    }}>
                    {tab === "communications" ? "Communications" : "Status History"}
                  </Button>
                ))}
              </Box>

              {activeTab === "communications" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: 240, overflowY: "auto" }}>
                  {!lead.communications?.length ? (
                    <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 3 }}>
                      No communications logged yet.
                    </Typography>
                  ) : (lead.communications as ILeadCommunication[]).map((c) => (
                    <Box key={c.id} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: COMM_TYPE_CONFIG[c.type].color, mt: 1, flexShrink: 0 }} />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                          <Box component="span"
                            sx={{ fontSize: 10, fontWeight: 700, px: 0.75, py: 0.15, borderRadius: 1,
                              color: COMM_TYPE_CONFIG[c.type].color, bgcolor: COMM_TYPE_CONFIG[c.type].bg }}>
                            {COMM_TYPE_CONFIG[c.type].label}
                          </Box>
                          {c.sent_by && <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>by {c.sent_by}</Typography>}
                          <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: "auto" }}>{dayjs(c.created_at).fromNow()}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>{c.message}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {activeTab === "history" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, maxHeight: 240, overflowY: "auto" }}>
                  {!lead.statusHistory?.length ? (
                    <Typography sx={{ fontSize: 13, color: "#94a3b8", textAlign: "center", py: 3 }}>No history yet.</Typography>
                  ) : lead.statusHistory!.map((h) => {
                    const nxt = STATUS_CONFIG[h.new_status as LeadStatus];
                    return (
                      <Box key={h.id} sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: nxt?.color ?? "#94a3b8", flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 12, color: "#374151" }}>
                          {h.old_status
                            ? (<><Box component="span" sx={{ color: "#94a3b8" }}>{STATUS_CONFIG[h.old_status as LeadStatus]?.label ?? h.old_status}</Box>{" → "}<Box component="span" sx={{ fontWeight: 700, color: nxt?.color }}>{nxt?.label ?? h.new_status}</Box></>)
                            : (<><Box component="span" sx={{ fontWeight: 700, color: nxt?.color }}>Created</Box> as {nxt?.label}</>)
                          }
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", ml: "auto" }}>{dayjs(h.created_at).fromNow()}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          </DialogContent>

          <Divider />
          <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
            <Button size="small" variant="contained"
              startIcon={<RocketLaunchOutlinedIcon sx={{ fontSize: 14 }} />}
              onClick={() => onConvertToQuote(lead)}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, bgcolor: BRAND, "&:hover": { bgcolor: "#2f3891" } }}>
              Convert to Quote
            </Button>
            <Button size="small" onClick={onClose}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b" }}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

// ── Main List Page ─────────────────────────────────────────────────────────────
const EnquiryList: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [currentTab, setCurrentTab]   = useState<0 | 1>(1);
  const [search, setSearch]           = useState("");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "">("");
  const [dateFrom, setDateFrom]       = useState("");
  const [dateTo, setDateTo]           = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [selectedId, setSelectedId]   = useState<number | null>(null);
  const [selected, setSelected]       = useState<number[]>([]);
  const [deleteId, setDeleteId]       = useState<number | null>(null);
  const [deleting, setDeleting]       = useState(false);
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [deleteLead] = useDeleteLeadMutation();
  const API_URL = process.env.REACT_APP_API_URL;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (search.trim())       params.search     = search.trim();
      if (statusFilter)        params.status     = statusFilter;
      if (dateFrom)            params.start_date = dateFrom;
      if (dateTo)              params.end_date   = dateTo;
      if (eventTypeFilter)     params.event_type = eventTypeFilter;
      if (sourceFilter)        params.source     = sourceFilter;
      const response = await axios.get(`${API_URL}/pdf/enquiry-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `enquiries-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err: any) {
      try {
        const blob: Blob = err?.response?.data;
        const text = blob ? await blob.text() : "";
        const parsed = JSON.parse(text || "{}");
        showErrorMessage(parsed?.message || "Failed to generate enquiry PDF report.");
      } catch {
        showErrorMessage("Failed to generate enquiry PDF report.");
      }
    } finally { setPdfLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      const res: any = await deleteLead(deleteId);
      if (res?.error || res?.data?.success === false) {
        showErrorMessage(res?.data?.message || "Failed to delete enquiry.");
      } else {
        showMessage("Enquiry deleted successfully.");
        setDeleteId(null);
      }
    } catch { showErrorMessage("Something went wrong."); }
    finally { setDeleting(false); }
  };

  const { data, isLoading } = useGetLeadsQuery({
    search:     search       || undefined,
    status:     statusFilter || undefined,
    date_from:  dateFrom     || undefined,
    date_to:    dateTo       || undefined,
    event_type: eventTypeFilter || undefined,
    source:     sourceFilter || undefined,
  });

  const leads: ILead[] = useMemo(() => {
    if (!data || !Array.isArray((data as any).data)) return [];
    return [...((data as any).data as ILead[])].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [data]);

  const stats = useMemo(() => ({
    total:     leads.length,
    new:       leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    qualified: leads.filter((l) => l.status === "qualified").length,
    converted: leads.filter((l) => l.status === "converted").length,
    lost:      leads.filter((l) => l.status === "lost").length,
  }), [leads]);

  const hasFilters = search || statusFilter || dateFrom || dateTo || eventTypeFilter || sourceFilter;


  const handleConvertToQuote = (lead: ILead) => {
    // If a real Customer was created during conversion, navigate directly to pre-select them
    if (lead.customer_id) {
      navigate(`/quote/create/${lead.customer_id}`);
      return;
    }
    // Fallback: pre-fill contact fields
    const params = new URLSearchParams({
      leadFirstName: lead.contact.firstname,
      leadLastName:  lead.contact.lastname  || "",
      leadPhone:     lead.contact.phone     || "",
      leadEmail:     lead.contact.email     || "",
      leadPickup:    lead.contact.route_pickup  || "",
      leadDropoff:   lead.contact.route_dropoff || "",
      leadEventDate: lead.contact.event_date    || "",
    });
    navigate(`/quote/create?${params.toString()}`);
  };

  const toggleSelect = (id: number) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleSelectAll = () =>
    setSelected(selected.length === leads.length ? [] : leads.map((l) => l.id));

  const STAT_CARDS = [
    { label: "Total Leads",  value: stats.total,     icon: <PersonOutlineIcon />,       color: BRAND,     bg: "#eef2ff", filter: "" as LeadStatus | "" },
    { label: "New",          value: stats.new,       icon: <NewReleasesOutlinedIcon />,  color: "#2f3891", bg: "#eef2ff", filter: "new"       as LeadStatus },
    { label: "Contacted",    value: stats.contacted, icon: <ChatBubbleOutlineIcon />,    color: "#f97316", bg: "#fff7ed", filter: "contacted"  as LeadStatus },
    { label: "Qualified",    value: stats.qualified, icon: <TrendingUpIcon />,           color: "#a855f7", bg: "#faf5ff", filter: "qualified"  as LeadStatus },
    { label: "Converted",    value: stats.converted, icon: <CheckCircleOutlineIcon />,   color: "#16a34a", bg: "#f0fdf4", filter: "converted"  as LeadStatus },
    { label: "Lost",         value: stats.lost,      icon: <CancelOutlinedIcon />,       color: "#ef4444", bg: "#fef2f2", filter: "lost"       as LeadStatus },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* ── Page header ── */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Enquiries & Leads</Typography>
          <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
            CRM-style lead management — track, qualify, and convert enquiries into quotes
          </Typography>
        </Box>
        {/* ── View toggle ── */}
        <Box sx={{ display: "flex", border: `1px solid ${BORDER}`, borderRadius: 2, overflow: "hidden", alignSelf: "center" }}>
          <Tooltip title="Calendar View" arrow>
            <Box onClick={() => setCurrentTab(1)}
              sx={{
                px: 1.5, py: 0.75, cursor: "pointer", display: "flex", alignItems: "center", gap: 0.75,
                bgcolor: currentTab === 1 ? "#eff6ff" : "transparent",
                borderRight: `1px solid ${BORDER}`,
                "&:hover": { bgcolor: currentTab === 1 ? "#eff6ff" : "#f1f5f9" },
              }}>
              <CalendarMonthOutlinedIcon sx={{ fontSize: 16, color: currentTab === 1 ? BRAND : "#64748b" }} />
              <Typography sx={{ fontSize: 12, fontWeight: currentTab === 1 ? 700 : 500, color: currentTab === 1 ? BRAND : "#64748b" }}>
                Calendar
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="List View" arrow>
            <Box onClick={() => setCurrentTab(0)}
              sx={{
                px: 1.5, py: 0.75, cursor: "pointer", display: "flex", alignItems: "center", gap: 0.75,
                bgcolor: currentTab === 0 ? "#eff6ff" : "transparent",
                "&:hover": { bgcolor: currentTab === 0 ? "#eff6ff" : "#f1f5f9" },
              }}>
              <FormatListBulletedIcon sx={{ fontSize: 16, color: currentTab === 0 ? BRAND : "#64748b" }} />
              <Typography sx={{ fontSize: 12, fontWeight: currentTab === 0 ? 700 : 500, color: currentTab === 0 ? BRAND : "#64748b" }}>
                List
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Calendar view ── */}
      {currentTab === 1 && (
        <CalendarView
          leads={(data as any)?.data ?? []}
          isLoading={isLoading}
          onLeadClick={(id) => navigate(`/enquiry/${id}`)}
        />
      )}

      {/* ── List tab ── */}
      {currentTab === 0 && <>

      {/* ── Stat cards ── */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {STAT_CARDS.map((s, i) => (
          <Card key={i} elevation={0}
            onClick={() => setStatusFilter(statusFilter === s.filter ? "" : s.filter)}
            sx={{
              flex: "1 1 110px", minWidth: 100, border: `2px solid`,
              borderColor: statusFilter === s.filter ? s.color : BORDER,
              borderRadius: 2.5, p: 2, cursor: "pointer",
              bgcolor: statusFilter === s.filter ? s.bg : "#fff",
              transition: "border-color 0.15s, background-color 0.15s",
              "&:hover": { borderColor: s.color, bgcolor: s.bg },
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <Box sx={{ width: 28, height: 28, borderRadius: 1.5, bgcolor: s.bg,
                display: "flex", alignItems: "center", justifyContent: "center", color: s.color }}>
                {React.cloneElement(s.icon, { sx: { fontSize: 15 } })}
              </Box>
              <Typography sx={{ fontSize: 10, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {s.label}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</Typography>
          </Card>
        ))}
      </Box>

      {/* ── Filter bar ── */}
      <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          {/* Search */}
          <TextField size="small" placeholder="Name / Phone / Email / Ref..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment> }}
            sx={{ minWidth: 240, flex: "2 1 240px" }} />

          {/* Status */}
          <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as LeadStatus | "")}
            displayEmpty sx={{ minWidth: 140, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff", flex: "1 1 140px" }}>
            <MenuItem value=""><em>All Statuses</em></MenuItem>
            {ALL_STATUSES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: STATUS_CONFIG[s].color }} />
                  {STATUS_CONFIG[s].label}
                </Box>
              </MenuItem>
            ))}
          </Select>

          {/* Event Type */}
          <Select size="small" value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)}
            displayEmpty sx={{ minWidth: 140, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff", flex: "1 1 140px" }}>
            <MenuItem value=""><em>All Events</em></MenuItem>
            {Object.entries(EVENT_TYPE_CONFIG).map(([k, v]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{v.label}</MenuItem>
            ))}
          </Select>

          {/* Source */}
          <Select size="small" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}
            displayEmpty sx={{ minWidth: 120, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff", flex: "1 1 120px" }}>
            <MenuItem value=""><em>All Sources</em></MenuItem>
            {Object.entries(SOURCE_CONFIG).map(([k, v]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 13 }}>{v.label}</MenuItem>
            ))}
          </Select>

          {/* Date range */}
          <TextField type="date" size="small" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }} label="From"
            sx={{ width: 145, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, height: 36 } }} />
          <TextField type="date" size="small" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }} label="To" inputProps={{ min: dateFrom || undefined }}
            sx={{ width: 145, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, height: 36 } }} />

          {hasFilters && (
            <Button size="small"
              onClick={() => { setSearch(""); setStatusFilter(""); setDateFrom(""); setDateTo(""); setEventTypeFilter(""); setSourceFilter(""); }}
              sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: `1px solid ${BORDER}` }}>
              Clear
            </Button>
          )}

          <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
            <Button size="small" variant="outlined"
              startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={() => exportToExcel(leads)}
              sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12,
                color: "#16a34a", borderColor: "#16a34a", "&:hover": { bgcolor: "#f0fdf4" } }}>
              Excel
            </Button>
            <Button size="small" variant="outlined"
              startIcon={pdfLoading ? <CircularProgress size={14} /> : <PictureAsPdfOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12,
                color: "#ef4444", borderColor: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
              PDF
            </Button>
            <Button size="small" variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={() => navigate("/enquiry/create")}
              sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12, bgcolor: BRAND, "&:hover": { bgcolor: "#2f3891" } }}>
              New Inquiry
            </Button>
          </Box>
        </Box>
      </Card>

      {/* ── Table ── */}
      <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 380px)", overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ ...thSx, bgcolor: "#f8fafc", width: 40 }}>
                  <Checkbox size="small" indeterminate={selected.length > 0 && selected.length < leads.length}
                    checked={leads.length > 0 && selected.length === leads.length}
                    onChange={toggleSelectAll} />
                </TableCell>
                {["#","Ref","Created","Customer","Phone","Email","Event","Event Date","Route","Status","Source","Actions"].map((h) => (
                  <TableCell key={h} sx={thSx}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={13} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} sx={{ textAlign: "center", py: 8 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                      <PersonOutlineIcon sx={{ fontSize: 44, color: "#cbd5e1" }} />
                      <Typography sx={{ fontSize: 14, color: "#94a3b8", fontWeight: 600 }}>No enquiries found</Typography>
                      <Typography sx={{ fontSize: 12, color: "#cbd5e1" }}>
                        Leads submitted through the enquiry form will appear here.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead, idx) => {
                  const st      = STATUS_CONFIG[lead.status];
                  const isNew   = lead.status === "new";
                  const evtConf = lead.contact.event_type ? EVENT_TYPE_CONFIG[lead.contact.event_type] : null;
                  const srcConf = lead.source ? SOURCE_CONFIG[lead.source] : null;
                  const formConf = lead.form_type ? FORM_TYPE_CONFIG[lead.form_type] : null;
                  return (
                    <TableRow key={lead.id}
                      onClick={() => navigate(`/enquiry/${lead.id}`)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { bgcolor: "#f0f9ff" },
                        "&:last-child td": { borderBottom: 0 },
                        ...(isNew ? { fontWeight: 700 } : {}),
                      }}>

                      {/* Checkbox */}
                      <TableCell padding="checkbox" sx={tdSx} onClick={(e) => { e.stopPropagation(); toggleSelect(lead.id); }}>
                        <Checkbox size="small" checked={selected.includes(lead.id)} />
                      </TableCell>

                      {/* # */}
                      <TableCell sx={{ ...tdSx, color: "#94a3b8", width: 36 }}>{idx + 1}</TableCell>

                      {/* Ref */}
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <TagIcon sx={{ fontSize: 11, color: "#94a3b8" }} />
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BRAND, fontFamily: "monospace" }}>
                            {lead.lead_ref}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Created */}
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontSize: 11, color: "#374151" }}>
                          {dayjs(lead.created_at).format("DD MMM YYYY")}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>
                          {dayjs(lead.created_at).fromNow()}
                        </Typography>
                      </TableCell>

                      {/* Customer */}
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: "#eef2ff", color: BRAND, fontSize: 11, fontWeight: 800, borderRadius: 1.5, flexShrink: 0 }}>
                            {getInitials(lead.contact.firstname, lead.contact.lastname)}
                          </Avatar>
                          <Typography sx={{ fontSize: 12, fontWeight: isNew ? 700 : 600, color: "#0f172a", whiteSpace: "nowrap" }}>
                            {lead.contact.firstname} {lead.contact.lastname || ""}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Phone */}
                      <TableCell sx={tdSx} onClick={(e) => e.stopPropagation()}>
                        {lead.contact.phone ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Tooltip title="Copy phone" arrow>
                              <Typography
                                component="span"
                                onClick={() => navigator.clipboard.writeText(lead.contact.phone!)}
                                sx={{ fontSize: 11, color: "#374151", cursor: "pointer", "&:hover": { color: BRAND } }}>
                                {lead.contact.phone}
                              </Typography>
                            </Tooltip>
                            <Tooltip title="WhatsApp" arrow>
                              <IconButton size="small"
                                component="a" href={`https://wa.me/${lead.contact.phone.replace(/\D/g, "")}`} target="_blank"
                                sx={{ p: 0.25, color: "#25d366" }}>
                                <WhatsAppIcon sx={{ fontSize: 13 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Email */}
                      <TableCell sx={tdSx} onClick={(e) => e.stopPropagation()}>
                        {lead.contact.email ? (
                          <Tooltip title="Copy email" arrow>
                            <Typography
                              component="span"
                              onClick={() => navigator.clipboard.writeText(lead.contact.email!)}
                              sx={{ fontSize: 11, color: "#374151", cursor: "pointer", "&:hover": { color: BRAND }, maxWidth: 160,
                                display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {lead.contact.email}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Event Type */}
                      <TableCell sx={tdSx}>
                        {evtConf ? (
                          <Box component="span"
                            sx={{ display: "inline-flex", alignItems: "center", px: 1, py: 0.2, borderRadius: 1,
                              fontSize: 10, fontWeight: 700, color: evtConf.color, bgcolor: evtConf.bg }}>
                            {evtConf.label}
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Event Date */}
                      <TableCell sx={tdSx}>
                        {lead.contact.event_date ? (
                          <Typography sx={{ fontSize: 11, color: "#374151" }}>
                            {dayjs(lead.contact.event_date).format("DD MMM YYYY")}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Route */}
                      <TableCell sx={tdSx}>
                        {lead.contact.route_pickup || lead.contact.route_dropoff ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <RouteOutlinedIcon sx={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 11, color: "#374151", whiteSpace: "nowrap" }}>
                              {lead.contact.route_pickup || "?"} → {lead.contact.route_dropoff || "?"}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={tdSx}>
                        <Box component="span"
                          sx={{ display: "inline-flex", alignItems: "center", px: 1.25, py: 0.3, borderRadius: 1.5,
                            fontSize: 11, fontWeight: 700, color: st.color, bgcolor: st.bg, border: `1px solid ${st.border}` }}>
                          {st.label}
                        </Box>
                      </TableCell>

                      {/* Source */}
                      <TableCell sx={tdSx}>
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.4 }}>
                          {srcConf ? (
                            <Box component="span"
                              sx={{ fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 1,
                                color: srcConf.color, bgcolor: srcConf.bg }}>
                              {srcConf.label}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 11, color: "#cbd5e1" }}>—</Typography>
                          )}
                          {formConf && (
                            <Box component="span"
                              sx={{ fontSize: 10, fontWeight: 700, px: 0.75, py: 0.2, borderRadius: 1,
                                color: formConf.color, bgcolor: formConf.bg }}>
                              {formConf.label}
                            </Box>
                          )}
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View" arrow>
                            <IconButton size="small" onClick={() => navigate(`/enquiry/${lead.id}`)}
                              sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: "#f0f9ff", color: "#0ea5e9", "&:hover": { bgcolor: "#e0f2fe" } }}>
                              <VisibilityOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Convert to Quote" arrow>
                            <IconButton size="small" onClick={() => handleConvertToQuote(lead)}
                              sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: "#eef2ff", color: BRAND, "&:hover": { bgcolor: "#e0e7ff" } }}>
                              <RocketLaunchOutlinedIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="WhatsApp" arrow>
                            <IconButton size="small"
                              component="a" href={lead.contact.phone ? `https://wa.me/${lead.contact.phone.replace(/\D/g, "")}` : "#"} target="_blank"
                              disabled={!lead.contact.phone}
                              sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: "#f0fdf4", color: "#25d366",
                                "&:hover": { bgcolor: "#dcfce7" }, "&.Mui-disabled": { opacity: 0.35 } }}>
                              <WhatsAppIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton size="small" onClick={() => setDeleteId(lead.id)}
                              sx={{ width: 26, height: 26, borderRadius: 1.5, bgcolor: "#fef2f2", color: "#ef4444", "&:hover": { bgcolor: "#fecaca" } }}>
                              <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Table footer */}
        {leads.length > 0 && (
          <Box sx={{ px: 2, py: 1.25, borderTop: `1px solid ${BORDER}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
              {selected.length > 0
                ? `${selected.length} of ${leads.length} selected`
                : `${leads.length} lead${leads.length !== 1 ? "s" : ""}`}
            </Typography>
            {selected.length > 0 && (
              <Button size="small" onClick={() => setSelected([])}
                sx={{ fontSize: 11, color: "#64748b", textTransform: "none", borderRadius: 2 }}>
                Clear selection
              </Button>
            )}
          </Box>
        )}
      </Card>

      </>}

      {/* ── Detail Modal (shared between list and calendar) ── */}
      <LeadDetailModal
        leadId={selectedId}
        onClose={() => setSelectedId(null)}
        onConvertToQuote={(lead) => { setSelectedId(null); handleConvertToQuote(lead); }}
      />

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={Boolean(deleteId)} onClose={() => !deleting && setDeleteId(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ height: 4, bgcolor: "#ef4444" }} />
        <DialogTitle sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a", pb: 0.5 }}>
          Delete Enquiry
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#475569" }}>
            Are you sure you want to delete this enquiry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button size="small" onClick={() => setDeleteId(null)} disabled={deleting}
            sx={{ textTransform: "none", fontSize: 13, borderRadius: 2, borderColor: BORDER, color: "#64748b", border: `1px solid ${BORDER}` }}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={13} color="inherit" /> : <DeleteOutlineIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontSize: 13, borderRadius: 2, bgcolor: "#ef4444", boxShadow: "none", "&:hover": { bgcolor: "#dc2626" } }}>
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnquiryList;
